import type { Address } from '../../../stores/useAppointmentsStore';
import type {
  Discount,
  Financials,
  LineItem,
  QuoteCustomerShort,
  QuoteDetail,
  QuoteDetailLinks,
  QuoteSignature,
} from '../../../stores/useQuotesStore';

/**
 * Adapter for the rich quote-detail wire returned by
 * `GET /v2/customers/:customer_id/quotes/:id`.
 *
 * Wire-contract drift triple-check (project memory `project_wire_contract_drift`):
 *
 *   1. jbuilder      — /Users/jane/Documents/Projects/api/app/views/v2/quotes/_quote.json.jbuilder
 *   2. permit/scope  — /Users/jane/Documents/Projects/api/app/controllers/v2/customer_quotes_controller.rb
 *                       `set_quote` uses `@customer.quotes.find(params[:id])`; the index/search scope
 *                       includes :actual_membership_program, :comparison_membership_program, :discount,
 *                       line_items, address, accepted_signature.
 *   3. legacy slice  — /Users/jane/Documents/Projects/consumer-portal/src/store.ts (lines 24, 82-92,
 *                       218-223 — `currentQuote` is a verbatim res.data write; `quoteProceed` POSTs
 *                       to a server-minted link). The legacy view reads `quote.financials.*`,
 *                       `quote.items_attributes`, `quote.discount_attributes`, `quote.links.quote_proceed`,
 *                       `quote.signature`, `quote.summary` (HTML, view-decoded), `quote.name`.
 *
 * Field-by-field drift check (each row = jbuilder field → adapter behaviour):
 *
 *   | jbuilder field                       | adapter behaviour                                       |
 *   | ------------------------------------ | ------------------------------------------------------- |
 *   | id, job_id, customer_id              | normalizeId — tolerates stringy ids; 0 for malformed    |
 *   | quote_type                           | passthrough (default 'single' when absent)              |
 *   | status (api_quote_status)            | lowercase normalization (defensive)                     |
 *   | name (nullable)                      | omit-vs-preserve (key dropped when null/undef)          |
 *   | summary (HTML, nullable)             | omit-vs-preserve; HTML verbatim (view decodes)          |
 *   | quote_date, sent_at                  | optional ISO passthrough                                |
 *   | editable, archived                   | optional boolean passthrough                            |
 *   | display_membership_savings           | optional boolean passthrough                            |
 *   | actual_membership_program_id/name    | omit-vs-preserve                                        |
 *   | comparison_membership_program_id/name| omit-vs-preserve (only emitted on two_column)           |
 *   | customer / billing_customer          | passthrough (customer_short shape)                      |
 *   | billing_address / service_address    | adaptAddress: street→address1, unit→address2 (omit if "")|
 *   | signature                            | omit-vs-preserve                                        |
 *   | items_attributes                     | empty-array default; per-item passthrough               |
 *   | discount_attributes                  | omit-vs-preserve (id/code/kind/percent)                 |
 *   | financials.*_cents                   | safeCents: Number.isFinite guard → 0 default            |
 *   | financials.savings_cents             | safeCents if present; omit if not                       |
 *   | links                                | omit-vs-preserve (HATEOAS gate at consumer)             |
 *   | payments_as_low_as_cents             | safeCents (jbuilder ships nil when not show_payments)   |
 *   | financing_options_active             | boolean (default false)                                 |
 *   | jobless_financing                    | boolean (default false)                                 |
 *
 * Without this adapter, drifted keys produce NaN money, undefined dates, and
 * blank pages with 200 OK. The 5.1 invoice adapter is the cautionary tale.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Wire interface — snake_case + nullable, mirrors the jbuilder field-by-field.
// Every nullable optional field carries `string | null` (or `number | null`)
// because the jbuilder emits explicit `nil` for missing rows; the adapter
// normalises both `null` and `undefined` to "key omitted from the domain".
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Address shape emitted via `v2/addresses/_address.json.jbuilder`. Only the
 * subset consumed by the quote-detail view is typed explicitly; the
 * `[extra: string]: unknown` index keeps the adapter forward-compatible with
 * future address fields without re-shaping the wire interface.
 */
export interface QuoteDetailWireAddress {
  id?: number;
  street?: string | null;
  unit?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  time_zone?: string | null;
  primary?: boolean;
  name?: string | null;
  invalid?: boolean;
  /** Other address fields are present on the wire but unused by the detail view. */
  [extra: string]: unknown;
}

/**
 * Customer projection emitted via `v2/customers/_customer_short.json.jbuilder`.
 * Used for both `customer` and `billing_customer` slots on the quote wire.
 */
export interface QuoteDetailWireCustomerShort {
  customer_id: number;
  type?: string;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  accounting_key?: string | null;
  authentication_token?: string | null;
  cable_token?: string | null;
  membership_status?: string | null;
}

/**
 * Line-item row emitted via `v2/items/_item.json.jbuilder`. `quantity` /
 * `unit_price_cents` / `total_cents` are coerced through `safeCents` at the
 * adapter boundary so the domain never carries NaN money. Comparison fields
 * only emit on two_column quotes; the adapter omits them otherwise.
 *
 * Grouping (260611-gfb, `?item_grouping=1`): a group node carries
 * `type:'group'` + `group_id` + `items_in_group` and its aggregate prices in
 * the SAME `unit_price_cents`/`total_cents` keys as a flat row. The
 * deprecated group-total mirror key is intentionally NOT typed/read
 * (Pitfall 1 from the invoice 05.6 work) — use the aggregate `total_cents`
 * above instead.
 */
export interface QuoteDetailWireLineItem {
  id: number;
  item_id?: number;
  item_type?: string;
  item_category_name?: string | null;
  unit_name?: string | null;
  quantity?: number;
  unit_price_cents?: number;
  total_cents?: number;
  description?: string | null;
  warranty?: string | null;
  comparison_price_cents?: number | null;
  comparison_total_cents?: number | null;
  type?: 'group' | 'lineItem' | null;
  group_id?: number | null;
  items_in_group?: QuoteDetailWireLineItem[] | null;
  links?: Record<string, { href?: string } | undefined>;
}

/**
 * Promo block emitted under `discount_attributes` (jbuilder lines 45-50).
 * `percent` is only emitted when `kind === 'percentage'`. Adapter preserves
 * the omit-vs-preserve semantics so view-layer `'percent' in discount` reads
 * cleanly.
 */
export interface QuoteDetailWireDiscount {
  id?: number;
  code?: string;
  kind?: string;
  percent?: number;
}

/**
 * Financial totals block (jbuilder lines 52-62). Every value is integer cents
 * on the canonical wire, but `number | string | null` is accepted at the type
 * boundary so the adapter can defensively coerce malformed payloads (test fixtures,
 * future API drift, partial wires).
 */
export interface QuoteDetailWireFinancials {
  subtotal_cents?: number | string | null;
  discount_amount_cents?: number | string | null;
  late_fee_cents?: number | string | null;
  sales_tax_cents?: number | string | null;
  total_cents?: number | string | null;
  savings_cents?: number | string | null;
}

/**
 * Customer-signed acceptance block (jbuilder lines 32-35 + v2/signatures/_signature.json.jbuilder).
 * Emitted only when `quote.signature` is present (i.e. an accepted quote).
 */
export interface QuoteDetailWireSignature {
  kind?: string;
  image_url?: string;
  signature_time?: string;
}

/**
 * Full quote-detail wire envelope. Each key is shaped after the jbuilder
 * partial; see the module header for the field-by-field drift-check table.
 *
 * The `id` field tolerates string-numeric values (defensive — matches the
 * `normalizeId` idiom shared with quoteRow.ts) and is the only top-level field
 * that is permitted to be a string on the wire.
 */
export interface QuoteDetailWire {
  id: number | string;
  job_id?: number;
  customer_id?: number;
  quote_type?: string;
  status?: string;
  name?: string | null;
  summary?: string | null;
  quote_date?: string | null;
  sent_at?: string | null;
  editable?: boolean;
  archived?: boolean;
  display_membership_savings?: boolean;
  actual_membership_program_id?: number | null;
  actual_membership_program_name?: string | null;
  comparison_membership_program_id?: number | null;
  comparison_membership_program_name?: string | null;
  customer?: QuoteDetailWireCustomerShort | null;
  billing_customer?: QuoteDetailWireCustomerShort | null;
  billing_address?: QuoteDetailWireAddress | null;
  service_address?: QuoteDetailWireAddress | null;
  signature?: QuoteDetailWireSignature | null;
  items_attributes?: QuoteDetailWireLineItem[] | null;
  discount_attributes?: QuoteDetailWireDiscount | null;
  financials?: QuoteDetailWireFinancials | null;
  payments_as_low_as_cents?: number | null;
  financing_options_active?: boolean;
  jobless_financing?: boolean;
  /**
   * Server-minted POST target for the proceed action — top-level field on the
   * wire (mirrors quoteRow.ts:111). The Rails detail endpoint emits this
   * alongside `links.quote_proceed.href`; the consumer prefers top-level.
   * Plan 15 (UAT Gap 2 round 3) — closed the wire-contract drift seam where
   * the detail adapter never extracted this field while the list adapter has
   * extracted it since Phase 5.4.
   */
  proceed_url?: string | null;
  links?: QuoteDetailLinks;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper idioms — copied from quoteRow.ts / appointment.ts.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Coerce a wire id (number or string-numeric) to a number. Returns 0 on NaN.
 * Mirrors quoteRow.ts normalizeId.
 */
function normalizeId(raw: number | string | undefined | null): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (raw === undefined || raw === null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Defensive *_cents coerce: NaN/Infinity/non-numeric/undefined → 0. Prevents
 * downstream `$NaN` renders when the wire ships malformed or missing money.
 */
function safeCents(raw: number | string | null | undefined): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (raw === null || raw === undefined) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Lowercase the wire status. Mirrors appointment.ts normaliseStatus shape but
 * without the cancelled/canceled dual-map — quotes ship a single canonical
 * status string via `api_quote_status`. Lowercasing absorbs the legacy
 * titleized variants ("Pending", "Denied", "Accepted", "Expired").
 */
function normaliseQuoteStatus(raw: string | undefined | null): string {
  return (raw ?? '').toLowerCase();
}

const EMPTY_ADDRESS: Address = {
  id: 0,
  address1: '',
  city: '',
  state: '',
  zip_code: '',
  time_zone: '',
};

function str(value: string | null | undefined, fallback = ''): string {
  return value ?? fallback;
}

/**
 * Map the v2 address wire to the shared `Address` shape (mirrors
 * appointment.ts:adaptAddress — street→address1, unit→address2 omitted when
 * empty). Reused for billing_address and service_address.
 */
function adaptAddress(
  wire: QuoteDetailWireAddress | null | undefined,
): Address {
  if (!wire) return { ...EMPTY_ADDRESS };
  const unit = (wire.unit ?? '').trim();
  const base: Address = {
    id: wire.id ?? 0,
    address1: str(wire.street),
    city: str(wire.city),
    state: str(wire.state),
    zip_code: str(wire.zip_code),
    time_zone: str(wire.time_zone),
  };
  if (wire.name != null) base.name = wire.name;
  if (typeof wire.invalid === 'boolean') base.invalid = wire.invalid;
  if (typeof wire.primary === 'boolean') base.primary = wire.primary;
  return unit ? { ...base, address2: unit } : base;
}

/**
 * Copy a key from src to dest only when its value is not null/undefined.
 * Encapsulates the omit-vs-preserve idiom that every customer_short /
 * QuoteDetail optional field uses.
 */
function preserveString<
  S extends Record<string, unknown>,
  D extends Record<string, unknown>,
>(src: S, dest: D, key: keyof S & keyof D & string): void {
  const v = src[key];
  if (v !== null && v !== undefined) {
    (dest as Record<string, unknown>)[key] = v;
  }
}

const CUSTOMER_SHORT_OPTIONAL_KEYS: ReadonlyArray<
  keyof QuoteDetailWireCustomerShort & keyof QuoteCustomerShort
> = [
  'type',
  'full_name',
  'first_name',
  'last_name',
  'email',
  'phone',
  'accounting_key',
  'authentication_token',
  'cable_token',
  'membership_status',
];

/**
 * customer_short passthrough — preserves the field shape verbatim with light
 * normalization (null → undefined for optional string fields so view-layer
 * `if (customer.email)` reads cleanly).
 */
function adaptCustomerShort(
  wire: QuoteDetailWireCustomerShort | null | undefined,
): QuoteCustomerShort {
  if (!wire) {
    return { customer_id: 0 };
  }
  const out: QuoteCustomerShort = {
    customer_id: wire.customer_id ?? 0,
  };
  for (const k of CUSTOMER_SHORT_OPTIONAL_KEYS) {
    preserveString(
      wire as unknown as Record<string, unknown>,
      out as unknown as Record<string, unknown>,
      k as string,
    );
  }
  return out;
}

/** Per-item adapter. Numbers coerced defensively; optional keys omit-vs-preserve. */
function adaptLineItem(wire: QuoteDetailWireLineItem): LineItem {
  const item: LineItem = {
    id: normalizeId(wire.id),
    quantity: safeCents(wire.quantity), // safeCents tolerates non-finite quantities
    unit_price_cents: safeCents(wire.unit_price_cents),
    total_cents: safeCents(wire.total_cents),
  };
  if (wire.item_id != null) item.item_id = wire.item_id;
  if (wire.item_type != null) item.item_type = wire.item_type;
  if (wire.item_category_name != null) {
    item.item_category_name = wire.item_category_name;
  }
  if (wire.unit_name != null) item.unit_name = wire.unit_name;
  if (wire.description != null) item.description = wire.description;
  if (wire.warranty != null) item.warranty = wire.warranty;
  if (wire.comparison_price_cents != null) {
    item.comparison_price_cents = safeCents(wire.comparison_price_cents);
  }
  if (wire.comparison_total_cents != null) {
    item.comparison_total_cents = safeCents(wire.comparison_total_cents);
  }
  if (wire.links) item.links = wire.links;
  return item;
}

/**
 * Group-node adapter (260611-gfb, mirrors invoiceDetail.ts:adaptGroupNode).
 * A `type:'group'` wire node maps to a leader `LineItem` carrying the group's
 * AGGREGATE prices (`unit_price_cents`/`total_cents` — never the deprecated
 * group-total mirror, Pitfall 1) plus `items_in_group`, the children
 * mapped through the UNTOUCHED `adaptLineItem` (children carry no price keys
 * for customers → `safeCents` 0-coerces them).
 *
 * The group node has no `unit_name`/`quantity`, so the leader's title-driving
 * fields + quantity derive from the FIRST child (omit-vs-preserve copy),
 * letting QuoteLineItemsCard's existing `decodeText(item.unit_name)` /
 * description / warranty render paths pick up the leader content unchanged.
 * An empty/absent `items_in_group` → `children[0]` undefined → qty 0, no crash.
 */
function adaptGroupNode(wire: QuoteDetailWireLineItem): LineItem {
  const children = (wire.items_in_group ?? []).map(adaptLineItem);
  const leader: LineItem = {
    id: normalizeId(wire.group_id),
    quantity: safeCents(children[0]?.quantity),
    unit_price_cents: safeCents(wire.unit_price_cents),
    total_cents: safeCents(wire.total_cents),
    type: 'group',
    group_id: wire.group_id ?? null,
    items_in_group: children,
  };
  const first = children[0];
  if (first) {
    if (first.unit_name != null) leader.unit_name = first.unit_name;
    if (first.item_category_name != null) {
      leader.item_category_name = first.item_category_name;
    }
    if (first.description != null) leader.description = first.description;
    if (first.warranty != null) leader.warranty = first.warranty;
    if (first.item_type != null) leader.item_type = first.item_type;
  }
  return leader;
}

/**
 * Dispatcher over a mixed `items_attributes` node (260611-gfb). Branches a
 * group node to `adaptGroupNode`; everything else (flat `type:'lineItem'` or
 * a 5.5 payload with no `type` key) falls through to the untouched flat
 * `adaptLineItem` — backward-compat with flat single/two-column quotes is
 * structurally guaranteed (the dispatcher only forks on `wire.type ===
 * 'group'`).
 */
function adaptLineItemNode(wire: QuoteDetailWireLineItem): LineItem {
  return wire.type === 'group' ? adaptGroupNode(wire) : adaptLineItem(wire);
}

/**
 * Financials adapter. Every cents field is routed through safeCents so the
 * view never renders `$NaN`. savings_cents is omitted when not present on the
 * wire (jbuilder only emits it for two_column or non-zero savings).
 */
function adaptFinancials(
  wire: QuoteDetailWireFinancials | null | undefined,
): Financials {
  const out: Financials = {
    subtotal_cents: safeCents(wire?.subtotal_cents),
    discount_amount_cents: safeCents(wire?.discount_amount_cents),
    late_fee_cents: safeCents(wire?.late_fee_cents),
    sales_tax_cents: safeCents(wire?.sales_tax_cents),
    total_cents: safeCents(wire?.total_cents),
  };
  if (wire?.savings_cents !== undefined && wire?.savings_cents !== null) {
    out.savings_cents = safeCents(wire.savings_cents);
  }
  return out;
}

/** Discount adapter — omit-vs-preserve at each key. percent only on percentage kind. */
function adaptDiscount(
  wire: QuoteDetailWireDiscount | null | undefined,
): Discount | undefined {
  if (!wire) return undefined;
  const out: Discount = {
    id: normalizeId(wire.id),
    code: wire.code ?? '',
    kind: wire.kind ?? 'amount',
  };
  if (wire.percent !== undefined && wire.percent !== null) {
    out.percent = wire.percent;
  }
  return out;
}

/** Signature passthrough — omit-vs-preserve at the key level only. */
function adaptSignature(
  wire: QuoteDetailWireSignature | null | undefined,
): QuoteSignature | undefined {
  if (!wire) return undefined;
  return {
    kind: wire.kind ?? '',
    image_url: wire.image_url ?? '',
    signature_time: wire.signature_time ?? '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main adapter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pure adapter: maps the jbuilder wire payload to the typed `QuoteDetail`
 * domain shape. All wire-contract drift is absorbed here. Downstream service
 * implementations (Plan 05.5-03) and store actions (Plan 05.5-04) bind
 * against this contract.
 *
 * Behaviour guarantees (cross-check the spec file `__tests__/quoteDetail.spec.ts`):
 *
 *   - Status is lowercased at the boundary so consumers can rely on
 *     `quote.status === 'pending' | 'denied' | 'accepted' | 'expired' | 'draft'`.
 *   - Every *_cents field is routed through `safeCents` and defaults to 0
 *     when the wire ships malformed or missing data — view layer never
 *     renders `$NaN`.
 *   - Optional fields with `null`/`undefined` wire values are OMITTED from
 *     the domain object entirely (the key is absent), so `'name' in quote`
 *     reads as a presence check at the view layer.
 *   - Empty arrays (e.g. `items_attributes: []`) are PRESERVED, never omitted —
 *     view-layer `v-if="items.length > 0"` reads correctly.
 *   - HTML strings (`summary`, `description`) are passed through verbatim.
 *     The view layer is responsible for safe rendering and entity decoding
 *     (Vue interpolation by default — never `v-html` for these fields).
 *   - HATEOAS links are preserved verbatim when present and omitted when
 *     absent — consumers gate Proceed/financing affordances on key presence.
 */
export function adaptQuoteDetail(wire: QuoteDetailWire): QuoteDetail {
  const detail: QuoteDetail = {
    id: normalizeId(wire.id),
    job_id: normalizeId(wire.job_id),
    customer_id: normalizeId(wire.customer_id),
    quote_type: wire.quote_type ?? 'single',
    status: normaliseQuoteStatus(wire.status),
    customer: adaptCustomerShort(wire.customer),
    billing_customer: adaptCustomerShort(wire.billing_customer),
    billing_address: adaptAddress(wire.billing_address),
    service_address: adaptAddress(wire.service_address),
    items_attributes: (wire.items_attributes ?? []).map(adaptLineItemNode),
    financials: adaptFinancials(wire.financials),
    payments_as_low_as_cents: safeCents(wire.payments_as_low_as_cents),
    financing_options_active: Boolean(wire.financing_options_active),
    jobless_financing: Boolean(wire.jobless_financing),
  };

  attachOptionalFields(detail, wire);
  return detail;
}

const OPTIONAL_STRING_KEYS: ReadonlyArray<
  keyof QuoteDetailWire & keyof QuoteDetail
> = ['name', 'summary', 'quote_date', 'sent_at'];

const OPTIONAL_BOOLEAN_KEYS: ReadonlyArray<
  keyof QuoteDetailWire & keyof QuoteDetail
> = ['editable', 'archived', 'display_membership_savings'];

/**
 * Attach every optional field that the wire only sometimes emits. Extracted
 * to keep `adaptQuoteDetail` under the project's cyclomatic-complexity
 * ceiling and mirror the appointment.ts split.
 */
function attachOptionalFields(
  target: QuoteDetail,
  wire: QuoteDetailWire,
): void {
  for (const k of OPTIONAL_STRING_KEYS) {
    preserveString(
      wire as unknown as Record<string, unknown>,
      target as unknown as Record<string, unknown>,
      k as string,
    );
  }
  for (const k of OPTIONAL_BOOLEAN_KEYS) {
    const v = wire[k];
    if (typeof v === 'boolean') {
      (target as unknown as Record<string, unknown>)[k as string] = v;
    }
  }

  attachMembershipPrograms(target, wire);

  const signature = adaptSignature(wire.signature);
  if (signature) target.signature = signature;

  const discount = adaptDiscount(wire.discount_attributes);
  if (discount) target.discount_attributes = discount;

  if (wire.links) target.links = wire.links;

  // Plan 15 (UAT Gap 2 round 3) — top-level proceed_url field mirrors
  // quoteRow.ts:111. Empty string is semantically "no link" (same as the
  // wire omitting the key), so we omit on null/undefined/'' rather than
  // preserve. The consumer at QuoteDetail.vue:proceedUrl chains `?? ''` so
  // an absent domain key reads as empty string at the view boundary. This
  // closes the wire-contract drift seam that Plan 14 chased symptomatically;
  // the actual root cause was that the detail adapter never extracted
  // wire.proceed_url at all, while quoteRow.ts had been extracting the same
  // field as snake_case top-level since Phase 5.4.
  if (typeof wire.proceed_url === 'string' && wire.proceed_url.length > 0) {
    target.proceed_url = wire.proceed_url;
  }
}

const MEMBERSHIP_PROGRAM_KEYS: ReadonlyArray<
  keyof QuoteDetailWire & keyof QuoteDetail
> = [
  'actual_membership_program_id',
  'actual_membership_program_name',
  'comparison_membership_program_id',
  'comparison_membership_program_name',
];

function attachMembershipPrograms(
  target: QuoteDetail,
  wire: QuoteDetailWire,
): void {
  for (const k of MEMBERSHIP_PROGRAM_KEYS) {
    preserveString(
      wire as unknown as Record<string, unknown>,
      target as unknown as Record<string, unknown>,
      k as string,
    );
  }
}
